// client/src/components/pages/EmailReports/AdvancedReportConfigModal.tsx (converted from .js, ST-00229)
//
// Create / edit a Custom (advanced, SQL + template) email report. RPT-11 (ST-00203): unticking
// "Enable automatic scheduling" switches an active schedule off (every schedule type), the
// stored "HH:MM:SS" time shows as HH:MM (was NaN:NaN), a failed schedule call is shown, and the
// page receives the report with the saved schedule merged in (see ./scheduleUtils).
// The .js also held an unused preview handler and its state (never rendered); they are gone.
import { Fragment, useState, useEffect } from 'react';
import type { IconType } from 'react-icons';
import { toast } from 'react-toastify';
import {
  FaTimes,
  FaArrowLeft,
  FaArrowRight,
  FaSpinner,
  FaInfoCircle,
  FaDatabase,
  FaPalette,
  FaClock,
  FaCheck,
  FaPlus,
  FaTrash,
} from 'react-icons/fa';
import api from '../../../services/api';
import QuerySelectorWithEditor from './QuerySelectorWithEditor';
import CreateEmailTemplateModal from './CreateEmailTemplateModal';
import ThemedSelect from '../../common/ThemedSelect';
import ThemedTimePicker from '../../common/ThemedTimePicker';
import { mergeScheduleIntoReport, saveReportSchedule, toScheduleTimeInput } from './scheduleUtils';
import type {
  EmailReportApiError,
  EmailReportDetail,
  EmailReportListRow,
  EmailTemplateOption,
  ScheduleFormData,
  ScheduleType,
} from '../../../types/clientPages/emailReports';

/** The query QuerySelectorWithEditor hands back (telegram_bi.queries, either naming). */
interface SelectedQuery {
  query_name?: string;
  QueryName?: string;
  queryName?: string;
}

interface AdvancedFormData {
  reportName: string;
  description: string;
  recipients: string[];
  isActive: boolean;
  queryId: number | null;
  emailTemplateId: number | null;
  selectedQuery: SelectedQuery | null;
}

interface AdvancedScheduleForm extends ScheduleFormData {
  cronExpression: string;
  intervalMinutes: number;
  scheduledDatetime: string;
}

interface AdvancedReportConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The saved list row, with the saved schedule merged in. */
  onSave: (report: EmailReportListRow) => void;
  /** The report being edited (GET /email-reports/:id); null/absent = create. */
  report?: EmailReportDetail | null;
}

interface StepDef {
  id: number;
  title: string;
  icon: IconType;
}

const SCHEDULE_TYPES: ScheduleType[] = ['daily', 'weekly', 'monthly', 'cron', 'recurring', 'once'];

const templateIdOf = (t: EmailTemplateOption): number | undefined => t.template_id || t.id;

const CRON_PRESETS = [
  { label: 'Every hour', value: '0 * * * *' },
  { label: 'Daily at 8am', value: '0 8 * * *' },
  { label: 'Daily at 6pm', value: '0 18 * * *' },
  { label: 'Weekdays at 8am', value: '0 8 * * 1-5' },
  { label: 'Monday 8am', value: '0 8 * * 1' },
  { label: '1st of month 8am', value: '0 8 1 * *' },
];

const AdvancedReportConfigModal = ({
  isOpen,
  onClose,
  onSave,
  report = null,
}: AdvancedReportConfigModalProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [templates, setTemplates] = useState<EmailTemplateOption[]>([]);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);

  const [formData, setFormData] = useState<AdvancedFormData>({
    reportName: '',
    description: '',
    recipients: [''],
    isActive: true,
    queryId: null,
    emailTemplateId: null,
    selectedQuery: null,
  });

  const [scheduleData, setScheduleData] = useState<AdvancedScheduleForm>({
    scheduleType: 'daily',
    scheduleTime: '08:00',
    timezone: 'Africa/Johannesburg',
    cronExpression: '0 8 * * *',
    intervalMinutes: 60,
    scheduledDatetime: '',
    isActive: false,
  });

  const steps: StepDef[] = [
    { id: 1, title: 'Basic Info', icon: FaInfoCircle },
    { id: 2, title: 'Query', icon: FaDatabase },
    { id: 3, title: 'Template', icon: FaPalette },
    { id: 4, title: 'Schedule & Review', icon: FaClock },
  ];

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setError(null);
      loadTemplates();

      if (report) {
        setFormData({
          reportName: report.name || report.report_name || '',
          description: report.description || '',
          recipients: report.recipients && report.recipients.length > 0 ? report.recipients : [''],
          isActive: report.is_active !== undefined ? report.is_active : true,
          queryId: report.query_id || null,
          emailTemplateId: report.email_template_id || null,
          selectedQuery: null,
        });
        if (report.schedule) {
          // pg TIME "HH:MM:SS": `new Date()` of it is an Invalid Date (NaN:NaN, RPT-11).
          setScheduleData({
            scheduleType: (report.schedule.schedule_type as ScheduleType) || 'daily',
            scheduleTime: toScheduleTimeInput(report.schedule.schedule_time),
            timezone: report.schedule.timezone || 'Africa/Johannesburg',
            cronExpression: report.schedule.cron_expression || '0 8 * * *',
            intervalMinutes: report.schedule.interval_minutes || 60,
            scheduledDatetime: report.schedule.scheduled_datetime || '',
            isActive: report.schedule.is_active || false,
          });
        }
      } else {
        setFormData({
          reportName: '',
          description: '',
          recipients: [''],
          isActive: true,
          queryId: null,
          emailTemplateId: null,
          selectedQuery: null,
        });
        setScheduleData({
          scheduleType: 'daily',
          scheduleTime: '08:00',
          timezone: 'Africa/Johannesburg',
          cronExpression: '0 8 * * *',
          intervalMinutes: 60,
          scheduledDatetime: '',
          isActive: false,
        });
      }
    }
  }, [isOpen, report]);

  const loadTemplates = async () => {
    try {
      const res = await api.emailReports.getTemplates();
      const body = res.data as { data?: EmailTemplateOption[] } | EmailTemplateOption[] | null;
      setTemplates((Array.isArray(body) ? body : body?.data) || []);
    } catch (err) {
      console.error('Error loading templates:', err);
    }
  };

  // Recipients management
  const handleRecipientChange = (index: number, value: string) => {
    const newRecipients = [...formData.recipients];
    newRecipients[index] = value;
    setFormData((prev) => ({ ...prev, recipients: newRecipients }));
  };

  const addRecipient = () => {
    setFormData((prev) => ({ ...prev, recipients: [...prev.recipients, ''] }));
  };

  const removeRecipient = (index: number) => {
    if (formData.recipients.length > 1) {
      setFormData((prev) => ({
        ...prev,
        recipients: prev.recipients.filter((_, i) => i !== index),
      }));
    }
  };

  // Validation
  const validateStep = (step: number) => {
    setError(null);
    switch (step) {
      case 1: {
        if (!formData.reportName.trim()) {
          setError('Report name is required');
          return false;
        }
        const validEmails = formData.recipients.filter((e) => e.trim());
        if (validEmails.length === 0) {
          setError('At least one recipient email is required');
          return false;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        for (const email of validEmails) {
          if (!emailRegex.test(email)) {
            setError(`Invalid email address: ${email}`);
            return false;
          }
        }
        return true;
      }
      case 2:
        if (!formData.queryId) {
          setError('Please select or create a query');
          return false;
        }
        return true;
      case 3:
        if (!formData.emailTemplateId) {
          setError('Please select or create an email template');
          return false;
        }
        return true;
      case 4:
        if (scheduleData.isActive) {
          if (scheduleData.scheduleType === 'cron' && !scheduleData.cronExpression.trim()) {
            setError('Cron expression is required');
            return false;
          }
          if (
            scheduleData.scheduleType === 'recurring' &&
            (!scheduleData.intervalMinutes || scheduleData.intervalMinutes <= 0)
          ) {
            setError('Interval must be a positive number');
            return false;
          }
          if (scheduleData.scheduleType === 'once' && !scheduleData.scheduledDatetime) {
            setError('Scheduled date and time is required');
            return false;
          }
        }
        return true;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(4, prev + 1));
    }
  };

  const handleBack = () => {
    setError(null);
    setCurrentStep((prev) => Math.max(1, prev - 1));
  };

  const handleSubmit = async () => {
    for (let step = 1; step <= 4; step++) {
      if (!validateStep(step)) {
        setCurrentStep(step);
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      const submitData = {
        reportName: formData.reportName.trim(),
        description: formData.description.trim(),
        reportType: 'advanced',
        queryId: formData.queryId,
        emailTemplateId: formData.emailTemplateId,
        recipients: formData.recipients.filter((e) => e.trim()),
        isActive: formData.isActive,
      };

      let response;
      if (report) {
        response = await api.emailReports.update(report.id, submitData);
      } else {
        response = await api.emailReports.create(submitData);
      }

      const savedReport = response.data as EmailReportListRow;

      // The schedule: saved when ticked (only the chosen type's own field is sent), switched
      // off when unticked on a report whose schedule is active (RPT-11: it used to keep
      // sending). The report itself is saved either way, so a failed schedule call is
      // reported and the modal still closes.
      const { schedule, error: scheduleError } = await saveReportSchedule(
        savedReport.id,
        scheduleData,
        !!report?.schedule?.is_active
      );
      if (scheduleError) {
        console.error('Error saving schedule:', scheduleError);
        toast.error(`The report was saved, but its schedule was not: ${scheduleError}`);
      }

      onSave(mergeScheduleIntoReport(savedReport, schedule));
      onClose();
    } catch (err) {
      setError((err as EmailReportApiError).response?.data?.error || 'Failed to save report');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center space-x-1">
      {steps.map((step, idx) => (
        <Fragment key={step.id}>
          <button
            onClick={() => {
              if (step.id <= currentStep) setCurrentStep(step.id);
            }}
            className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              step.id === currentStep
                ? 'bg-primary-500 text-white'
                : step.id < currentStep
                  ? 'bg-primary-100 text-primary-700 hover:bg-primary-200'
                  : 'bg-gray-100 text-gray-400'
            }`}
          >
            {step.id < currentStep ? (
              <FaCheck className="w-3 h-3" />
            ) : (
              <step.icon className="w-3 h-3" />
            )}
            <span>{step.title}</span>
          </button>
          {idx < steps.length - 1 && (
            <div
              className={`w-8 h-0.5 ${step.id < currentStep ? 'bg-primary-400' : 'bg-gray-200'}`}
            />
          )}
        </Fragment>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Report Name *</label>
        <input
          type="text"
          value={formData.reportName}
          onChange={(e) => setFormData((prev) => ({ ...prev, reportName: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          placeholder="e.g., Daily Weight Analysis Report"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          rows={2}
          placeholder="Optional description"
        />
      </div>

      {/* Recipients */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Recipients *</label>
        {formData.recipients.map((recipient, index) => (
          <div key={index} className="flex items-center space-x-2 mb-2">
            <input
              type="email"
              value={recipient}
              onChange={(e) => handleRecipientChange(index, e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="email@example.com"
            />
            {formData.recipients.length > 1 && (
              <button
                onClick={() => removeRecipient(index)}
                className="text-red-500 hover:text-red-700 p-2"
              >
                <FaTrash className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
        <button
          onClick={addRecipient}
          className="text-primary-600 hover:text-purple-800 flex items-center space-x-1.5 text-sm"
        >
          <FaPlus className="w-3 h-3" />
          <span>Add Recipient</span>
        </button>
      </div>

      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="advIsActive"
          checked={formData.isActive}
          onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <label htmlFor="advIsActive" className="text-sm text-gray-700">
          Report is active
        </label>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div>
      <h3 className="text-sm font-medium text-gray-700 mb-3">
        Select or create a SQL query to power this report
      </h3>
      <QuerySelectorWithEditor
        selectedQueryId={formData.queryId}
        onQuerySelect={(id: number | null) => setFormData((prev) => ({ ...prev, queryId: id }))}
        onQueryData={(q: SelectedQuery | null) =>
          setFormData((prev) => ({ ...prev, selectedQuery: q }))
        }
      />
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Select Email Template *
        </label>
        <div className="flex items-center space-x-2">
          <ThemedSelect
            value={formData.emailTemplateId || ''}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                emailTemplateId: parseInt(String(e.target.value)) || null,
              }))
            }
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="">-- Select a template --</option>
            {templates.map((t) => (
              <option key={templateIdOf(t)} value={templateIdOf(t)}>
                {t.template_name || t.templateName}
              </option>
            ))}
          </ThemedSelect>
          <button
            onClick={() => setShowCreateTemplate(true)}
            className="px-3 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors flex items-center space-x-1.5 whitespace-nowrap"
          >
            <FaPlus className="w-3 h-3" />
            <span>New Template</span>
          </button>
        </div>
      </div>

      {templates.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-700">
          No email templates found. Create one to continue.
        </div>
      )}

      {formData.emailTemplateId && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <p className="text-xs text-gray-500 mb-1">Selected template</p>
          <p className="text-sm font-medium text-gray-800">
            {templates.find((t) => templateIdOf(t) === formData.emailTemplateId)?.template_name ||
              'Unknown template'}
          </p>
        </div>
      )}

      <CreateEmailTemplateModal
        isOpen={showCreateTemplate}
        onClose={() => setShowCreateTemplate(false)}
        onCreated={(newTemplate: { data?: EmailTemplateOption } | EmailTemplateOption) => {
          const tmpl: EmailTemplateOption =
            ('data' in newTemplate && newTemplate.data) || (newTemplate as EmailTemplateOption);
          setTemplates((prev) => [...prev, tmpl]);
          setFormData((prev) => ({ ...prev, emailTemplateId: templateIdOf(tmpl) ?? null }));
          setShowCreateTemplate(false);
        }}
      />
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-5">
      {/* Schedule Toggle */}
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-2">
          <input
            type="checkbox"
            id="advEnableSchedule"
            checked={scheduleData.isActive}
            onChange={(e) => setScheduleData((prev) => ({ ...prev, isActive: e.target.checked }))}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          <label htmlFor="advEnableSchedule" className="text-sm font-medium text-gray-700">
            Enable automatic scheduling
          </label>
        </div>
      </div>

      {scheduleData.isActive && (
        <div className="space-y-4">
          {/* Schedule Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Schedule Type</label>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              {SCHEDULE_TYPES.map((type) => (
                <button
                  key={type}
                  onClick={() => setScheduleData((prev) => ({ ...prev, scheduleType: type }))}
                  className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                    scheduleData.scheduleType === type
                      ? 'bg-primary-500 text-white border-primary-500'
                      : 'bg-surface text-gray-600 border-gray-300 hover:border-primary-300'
                  }`}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Basic schedule types */}
          {['daily', 'weekly', 'monthly'].includes(scheduleData.scheduleType) && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                <ThemedTimePicker
                  value={scheduleData.scheduleTime}
                  onChange={(e) =>
                    setScheduleData((prev) => ({ ...prev, scheduleTime: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                <ThemedSelect
                  value={scheduleData.timezone}
                  onChange={(e) =>
                    setScheduleData((prev) => ({ ...prev, timezone: String(e.target.value) }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="Africa/Johannesburg">Africa/Johannesburg (SAST)</option>
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">America/New_York (EST/EDT)</option>
                  <option value="Europe/London">Europe/London (GMT/BST)</option>
                </ThemedSelect>
              </div>
            </div>
          )}

          {/* Cron */}
          {scheduleData.scheduleType === 'cron' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cron Expression
                </label>
                <input
                  type="text"
                  value={scheduleData.cronExpression}
                  onChange={(e) =>
                    setScheduleData((prev) => ({ ...prev, cronExpression: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 font-mono text-sm"
                  placeholder="0 8 * * *"
                />
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-2">Quick presets:</p>
                <div className="flex flex-wrap gap-2">
                  {CRON_PRESETS.map((preset) => (
                    <button
                      key={preset.value}
                      onClick={() =>
                        setScheduleData((prev) => ({ ...prev, cronExpression: preset.value }))
                      }
                      className={`px-2 py-1 rounded text-xs border transition-colors ${
                        scheduleData.cronExpression === preset.value
                          ? 'bg-primary-100 border-primary-300 text-primary-700'
                          : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-primary-200'
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                <ThemedSelect
                  value={scheduleData.timezone}
                  onChange={(e) =>
                    setScheduleData((prev) => ({ ...prev, timezone: String(e.target.value) }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="Africa/Johannesburg">Africa/Johannesburg (SAST)</option>
                  <option value="UTC">UTC</option>
                  <option value="America/New_York">America/New_York (EST/EDT)</option>
                  <option value="Europe/London">Europe/London (GMT/BST)</option>
                </ThemedSelect>
              </div>
            </div>
          )}

          {/* Recurring */}
          {scheduleData.scheduleType === 'recurring' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Interval (minutes)
                </label>
                <input
                  type="number"
                  value={scheduleData.intervalMinutes}
                  onChange={(e) =>
                    setScheduleData((prev) => ({
                      ...prev,
                      intervalMinutes: parseInt(e.target.value) || 60,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  min="5"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Every{' '}
                  {scheduleData.intervalMinutes >= 60
                    ? `${Math.floor(scheduleData.intervalMinutes / 60)}h ${scheduleData.intervalMinutes % 60 > 0 ? `${scheduleData.intervalMinutes % 60}m` : ''}`
                    : `${scheduleData.intervalMinutes} minutes`}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                <ThemedSelect
                  value={scheduleData.timezone}
                  onChange={(e) =>
                    setScheduleData((prev) => ({ ...prev, timezone: String(e.target.value) }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="Africa/Johannesburg">Africa/Johannesburg (SAST)</option>
                  <option value="UTC">UTC</option>
                </ThemedSelect>
              </div>
            </div>
          )}

          {/* One-time */}
          {scheduleData.scheduleType === 'once' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date & Time</label>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="date"
                  value={(scheduleData.scheduledDatetime || '').split('T')[0] || ''}
                  onChange={(e) => {
                    const timePart =
                      (scheduleData.scheduledDatetime || '').split('T')[1] || '08:00';
                    setScheduleData((prev) => ({
                      ...prev,
                      scheduledDatetime: e.target.value ? `${e.target.value}T${timePart}` : '',
                    }));
                  }}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                />
                <ThemedTimePicker
                  value={
                    (scheduleData.scheduledDatetime || '').split('T')[1]?.substring(0, 5) || '08:00'
                  }
                  onChange={(e) => {
                    const datePart = (scheduleData.scheduledDatetime || '').split('T')[0] || '';
                    setScheduleData((prev) => ({
                      ...prev,
                      scheduledDatetime: datePart ? `${datePart}T${e.target.value}` : '',
                    }));
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Summary */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Report Summary</h4>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-500">Name:</span>{' '}
            <span className="font-medium">{formData.reportName}</span>
          </div>
          <div>
            <span className="text-gray-500">Type:</span>{' '}
            <span className="font-medium text-primary-600">Custom Report</span>
          </div>
          <div>
            <span className="text-gray-500">Query:</span>{' '}
            <span className="font-medium">
              {formData.selectedQuery?.query_name ||
                formData.selectedQuery?.QueryName ||
                formData.selectedQuery?.queryName ||
                'Selected'}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Template:</span>{' '}
            <span className="font-medium">
              {templates.find((t) => templateIdOf(t) === formData.emailTemplateId)?.template_name ||
                'Selected'}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Recipients:</span>{' '}
            <span className="font-medium">
              {formData.recipients.filter((r) => r.trim()).length}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Schedule:</span>{' '}
            <span className="font-medium">
              {scheduleData.isActive
                ? scheduleData.scheduleType === 'cron'
                  ? `Cron: ${scheduleData.cronExpression}`
                  : scheduleData.scheduleType === 'recurring'
                    ? `Every ${scheduleData.intervalMinutes} min`
                    : scheduleData.scheduleType === 'once'
                      ? `Once: ${scheduleData.scheduledDatetime}`
                      : `${scheduleData.scheduleType} at ${scheduleData.scheduleTime}`
                : 'Manual only'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-surface rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-5 border-b">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {report ? 'Edit Custom Report' : 'Create Custom Report'}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">SQL-powered report with custom template</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2">
            <FaTimes />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="p-4 border-b bg-gray-50">{renderStepIndicator()}</div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-5 border-t bg-gray-50">
          <div>
            {currentStep > 1 && (
              <button
                onClick={handleBack}
                className="px-4 py-2 text-gray-700 bg-surface border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors flex items-center space-x-2"
                disabled={loading}
              >
                <FaArrowLeft className="w-3 h-3" />
                <span>Back</span>
              </button>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-surface border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            {currentStep < 4 ? (
              <button
                onClick={handleNext}
                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors flex items-center space-x-2"
                disabled={loading}
              >
                <span>Next</span>
                <FaArrowRight className="w-3 h-3" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center space-x-2"
                disabled={loading}
              >
                {loading && <FaSpinner className="animate-spin" />}
                <span>{loading ? 'Saving...' : report ? 'Update Report' : 'Create Report'}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedReportConfigModal;
